import { DynamicStandaloneArticleDate } from '@/components/ArticleDate';
import { DynamicStandaloneByline } from '@/components/Byline';
import { DynamicStandaloneImage } from '@/components/Image';
import { DynamicStandaloneSaveArticleButton } from '@/components/SaveArticleButton';
import { DynamicStandaloneTag } from '@/components/Tag';
import { useStable, useStableProps } from '@/hooks/useStable';
import { ClassNameProvider, componentTheme } from '@/styles/ArticleHeader';
import { mergeOptions, mergeProps } from '@/utils/merge';
import { StandaloneArticleHeader } from 'base/components/ArticleHeader';
import { ArticleHeader } from 'base/components/ArticleHeader/ArticleHeader';
import { useMemo } from 'react';

export const ElleStandaloneArticleHeader: typeof StandaloneArticleHeader = ({
  bylines,
  category,
  description,
  headline,
  image,
  tags,
  datetime,
  options,
  ...props
}) => {
  const {
    $bylines,
    $date,
    $category: categoryProps,
    $description,
    $headline,
    $image,
    $tags: tagsProps,
    ...$base
  } = options ?? {};

  const { $tag: $categoryTag, ...$category } = categoryProps ?? {};
  const { $tag, ...$tags } = tagsProps ?? {};

  const memoizedTags = useMemo(
    () =>
      tags?.map((tag, index) => (
        <DynamicStandaloneTag
          key={index}
          link={{ href: `/etikett/${tag.value}` }}
          {...mergeProps(
            {
              options: {
                variant: 'text',
                theme: componentTheme('tag'),
                ...$tag,
              },
            },
            tag
          )}
        />
      )),
    [tags, $tag]
  );

  const memoCategoryTag = useMemo(
    () => (
      <DynamicStandaloneTag
        value={category?.value}
        link={{ href: category?.url || `etikett/${category?.value}` }}
        options={mergeOptions(
          {
            variant: 'text',
            theme: componentTheme('tag'),
          },
          $categoryTag
        )}
      />
    ),
    [$categoryTag, category?.url, category?.value]
  );

  const stableImageProps = useStableProps({ fill: true, priority: true }, image);

  const stableProviderProps = useStable({ size: 'default', colors: 'default', ...$base }, { variant: 'third' });

  return (
    <ClassNameProvider value={stableProviderProps}>
      <ArticleHeader {...$base} {...props}>
        {tags && tags.length > 0 && <ArticleHeader.Tags {...$tags}>{memoizedTags}</ArticleHeader.Tags>}
        {headline && <ArticleHeader.Headline {...$headline}>{headline}</ArticleHeader.Headline>}
        {category?.value && <ArticleHeader.Category {...$category}>{memoCategoryTag}</ArticleHeader.Category>}
        {datetime?.publishedDatetime && (
          <ArticleHeader.Date {...$date}>
            <DynamicStandaloneArticleDate {...datetime} />
          </ArticleHeader.Date>
        )}
        {bylines && bylines?.length > 0 && (
          <ArticleHeader.Bylines {...$bylines}>
            {bylines?.map((byline, index) => (
              <DynamicStandaloneByline {...byline} key={index} />
            ))}
          </ArticleHeader.Bylines>
        )}
        {image && (
          <ArticleHeader.Image {...$image}>
            <DynamicStandaloneImage {...stableImageProps} />
          </ArticleHeader.Image>
        )}
        {description && <ArticleHeader.Description {...$description}>{description}</ArticleHeader.Description>}
        <DynamicStandaloneSaveArticleButton />
      </ArticleHeader>
    </ClassNameProvider>
  );
};

export default ElleStandaloneArticleHeader;
